<template>
  <div class="map-container" ref="boxMap">
    <GmapMap
      v-if="showMap"
      ref="mapRef"
      class="map"
      :zoom="3"
      :center="{ lat: 0, lng: 0 }"
      @dragstart="handleDragStart"
      @dragend="handleDragEnd"
      style="width: 100%; height: 71.8rem"
      :options="{
        clickableIcons: false,
        minZoom: 2
      }"
      @idle="handleMapIdle"
      @click.native="handleMapClick">
      <GmapInfoWindow ref="infoWindow" :options="infoOptions" :position="infoWindowPos" :opened="infoWinOpen" @closeclick="infoWinOpen=false"/>
      <GmapCluster
        :averageCenter="true"
        :minimumClusterSize="clusterOptions.minimumClusterSize"
        :zoomOnClick="true"
        :gridSize="clusterOptions.gridSize"
        :styles="setClusterImage"
        ref="cluster">
        <GmapCustomMarker
          class="marker"
          v-for="(hotel, i) in hotels"
          :key="`${i}-${hotel.code}`"
          :marker="hotel.coordinates"
          :alignment="'center'"
          @click.native="!arePricesLoading && toggleInfoWindow(hotel,i)">
          <div v-if="arePricesLoading" class="custom-marker loading"><b-spinner></b-spinner></div>
          <div
            v-else
            class="custom-marker"
            :class="!getPrice(hotel).priceFrom.amount ? 'sold-out' : ''">
            {{getPriceString(hotel)}}
          </div>
        </GmapCustomMarker>
      </GmapCluster>
    </GmapMap>
    <button class="btn center-map-btn" @click="setCenter">{{translate('offMapCenterMap')}}</button>
  </div>
</template>

<script>
import Vue from 'vue'
import { gmapApi } from 'vue2-google-maps'
import { tripAdvisorService } from '@/infrastructure/Factory'
import { clusterImage } from '@/infrastructure/ClusteredMapUtils'
import PriceMapInfoWindow from '@/components/multi-offers/PriceMapInfoWindow'
import { getRatingImageForNumber } from '@/infrastructure/Tripadvisor'
import { translate } from 'rhg-vue-common'

const InfoWindow = Vue.extend(PriceMapInfoWindow)

const emptyInfoWindow = new InfoWindow({
  propsData: {
    marker: {},
    isLoading: true
  }
}).$mount()

export default {
  name: 'PriceMap',
  props: {
    hotels: Array,
    clusterOptions: {
      type: Object,
      required: true
    },
    prices: Array,
    zoom: Number
  },
  data: () => ({
    map: null,
    isDragging: false,
    arePricesLoading: true,
    bounds: {},
    infoWindowPos: null,
    infoWinOpen: false,
    currentId: null,
    infoOptions: {
      content: '',
      pixelOffset: {
        width: 0,
        height: -38
      }
    },
    showMap: false
  }),

  async mounted () {
    this.handleScrollMap()
    window.addEventListener('scroll', await this.handleScrollMap)
    window.addEventListener('touchend', await this.handleScrollMap)
  },
  methods: {
    fitMapBounds: async function () {
      if (!this.$refs.mapRef || !this.hotels.length) {
        return
      }

      await this.$gmapApiPromiseLazy()

      this.bounds = new this.google.maps.LatLngBounds()

      let latLngCoordinates

      this.hotels.forEach(hotel => {
        latLngCoordinates = new this.google.maps.LatLng({
          lat: hotel.coordinates.latitude,
          lng: hotel.coordinates.longitude
        })

        this.bounds.extend(latLngCoordinates)
      })

      if (this.map) {
        if (this.zoom) {
          this.map.setCenter(this.bounds.getCenter())
          this.map.setZoom(this.zoom)
        } else {
          this.map.fitBounds(this.bounds, this.clusterOptions.markerPadding)
        }
      }
    },
    setCenter: function () {
      this.infoWinOpen = false

      this.fitMapBounds()
    },
    handleMapClick: function ({ target }) {
      const classes = target.classList
      const isClickingMarker = classes.contains('custom-marker') || classes.contains('gm-style-iw') ||
        (target.className.includes('pricemap-infowindow') || target.parentElement.className.includes('pricemap-infowindow'))

      if (!isClickingMarker) {
        this.resetInfoWindow()
      }
    },
    handleMapIdle: function () {
      const markerClusterer = this.$refs.cluster.$clusterObject

      markerClusterer.repaint()
    },
    handleDragStart: function () {
      this.isDragging = true
      this.resetInfoWindow()
    },
    resetInfoWindow: function () {
      this.infoWindowPos = null
      this.infoWinOpen = false
      this.currentId = null
      this.infoOptions.content = ''
    },
    handleDragEnd: function () {
      this.isDragging = false
    },
    toggleInfoWindow: async function (marker, id) {
      let tripAdvisorRating = {}

      const price = this.getPrice(marker)

      if (this.isDragging) {
        this.isDragging = false
        return
      }

      if (this.currentId === id) {
        this.resetInfoWindow()
        return
      }

      this.infoWindowPos = {
        lat: marker.coordinates.latitude,
        lng: marker.coordinates.longitude
      }

      this.infoOptions.content = emptyInfoWindow.$el.outerHTML
      this.infoWinOpen = true
      this.currentId = id

      tripAdvisorRating = await tripAdvisorService.getTripAdvisorSingleHotelRating(marker.code)
      tripAdvisorRating = {
        ...tripAdvisorRating,
        image: getRatingImageForNumber(tripAdvisorRating.bubbleRating)
      }

      const infoWindow = new InfoWindow({
        store: this.$store,
        propsData: {
          marker,
          tripAdvisorRating,
          isLoading: false,
          price
        }
      }).$mount()

      const infoWindowBookNowBtn = infoWindow.$el.querySelector('button.pricemap-infowindow__btn')

      if (infoWindowBookNowBtn) {
        infoWindowBookNowBtn.setAttribute('onclick', `window.showOfferCalendarByHotel("${marker.code}")`)
      }

      this.infoOptions.content = infoWindow.$el.outerHTML
    },
    translateLabel: function (label) {
      return translate(label)
    },
    getPriceString: function (hotel) {
      const hotelPrice = this.getPrice(hotel)

      if (!hotelPrice) {
        return '...'
      } else {
        const isHotelSoldOut = !hotelPrice.priceFrom.amount
        return isHotelSoldOut ? this.translateLabel('offMapSoldOut') : hotelPrice.priceFrom.formattedAmount
      }
    },
    getPrice: function (hotel) {
      return this.prices.find(price => price.code === hotel.code)
    },
    async initializeMap () {
      window._loadMapsScript(async () => {
        window.vueGoogleMapsInit()
        this.map = await this.$refs.mapRef.$mapPromise
        this.fitMapBounds()
        this.$emit('onGoogleInit', this.google)
      })
    },
    async handleScrollMap () {
      const scrollPosition = window.scrollY + window.innerHeight
      const mapTop = this.$refs.boxMap.getBoundingClientRect().top + window.scrollY

      if (scrollPosition >= mapTop && !this.showMap) {
        this.showMap = true
        await this.initializeMap()
        this.removeListeners()
      }
    },
    async removeListeners () {
      window.removeEventListener('scroll', await this.handleScrollMap)
      window.removeEventListener('touchend', await this.handleScrollMap)
    }
  },
  computed: {
    google: gmapApi,
    setClusterImage: function () {
      return clusterImage
    }
  },
  watch: {
    hotels: function () {
      this.fitMapBounds()
    },
    prices: function (value) {
      this.arePricesLoading = !value.length
    }
  }
}
</script>

<style lang="scss">
  .map-container {
    height: 71.8rem;
    position: relative;
  }

  .center-map-btn {
    position: absolute;
    bottom: 6rem;
    right: 8rem;
    padding: 1.3rem 1.6rem;
    background-color: white;
    border-radius: 5rem;
    border: 0.1rem solid black;
    font-size: 1.2rem;
  }

  .map {
    height: 100%;
    width: 100%;

    &:before {
      content: "";
      height: 10rem;
      left: 0;
      position: absolute;
      width: 100%;
      z-index: zindex("z-1");
      pointer-events: none;
      background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0))
    }
  }

  .cluster {
    transform: translate(0, -50%);
  }

  .cluster > div {
    font-size: 2rem !important;
    font-weight: 300 !important;
    line-height: 3.2rem !important;
    top: 0.4rem !important;
  }

  .custom-marker {
    background: #FFFFFF;
    color: #53565A;
    height: 32px;
    border: 1px solid #53565A;
    border-radius: 2rem;
    padding: 0.8rem 1.6rem;
    font-weight: 700;
    font-size: 1.2rem;

    &.loading {
      padding: 0 1.6rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &.sold-out {
      background-color: #D8D8D8;
    }

    .spinner-border {
      height: 1.8rem;
      width: 1.8rem;
      border-width: 0.2em;
      color: color('grey-cool-two');
    }

    &:hover {
      cursor: pointer;
      color: color("black");
      border: 0.1rem solid color("black");
    }
  }

  .no-availability {
    background: #D8D8D8;
  }
</style>
